import { useState } from "react";
import { codeErrorMessage, platformLabel, type ApprovalState, type DeviceInfo } from "@helm/core";
import { Brand } from "../components/Brand";

type Handlers = {
  onSubmitCode: (code: string) => void;
  /** Re-register: mints a fresh first-device code once the old one has expired. */
  onNewCode: () => void;
  onRetry: () => void;
  onSignOut: () => void;
};

/**
 * Full-screen "Approve this device" flow (SPEC §20). Presentational: the state comes from
 * `DeviceGate` (or the dev preview).
 */
export function ApproveDeviceScreen({ state, ...h }: { state: ApprovalState } & Handlers): React.JSX.Element {
  let body: React.JSX.Element;
  switch (state.phase) {
    case "registering":
    case "approved":
      body = (
        <p className="muted" role="status">
          <span className="spinner" aria-hidden="true" /> Checking this device…
        </p>
      );
      break;
    case "failed":
      body = (
        <>
          <p className="login-error" role="alert">
            Could not register this device: {state.message}
          </p>
          <button type="button" className="btn btn-primary" onClick={h.onRetry}>
            Try again
          </button>
        </>
      );
      break;
    case "code":
      body = <CodeStep state={state} {...h} />;
      break;
    case "trusted":
      body = <TrustedStep device={state.device} />;
      break;
  }
  return (
    <div className="login gate">
      <section className="login-card gate-card" aria-labelledby="gate-title">
        <Brand as="div" className="login-brand" />
        <h1 className="gate-title" id="gate-title">
          Approve this device
        </h1>
        {body}
        <button type="button" className="btn gate-signout" onClick={h.onSignOut}>
          Sign out
        </button>
      </section>
    </div>
  );
}

function DeviceChip({ device }: { device: DeviceInfo }): React.JSX.Element {
  return (
    <div className="device-chip">
      <span className="device-chip-name">{device.name}</span>
      <span className="muted">
        {platformLabel(device.platform)} · id {device.id.slice(0, 8)}
      </span>
    </div>
  );
}

function CodeStep({
  state,
  onSubmitCode,
  onNewCode,
}: { state: Extract<ApprovalState, { phase: "code" }> } & Handlers): React.JSX.Element {
  const [code, setCode] = useState("");
  const limited = state.error?.kind === "rate_limited";
  return (
    <form
      className="gate-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmitCode(code);
      }}
    >
      <DeviceChip device={state.device} />
      <p>
        This is the first device on your account. When it registered, <code>helm-api</code> printed a one-time
        6-digit code in its log. Enter it here to prove you run the server.
      </p>
      <details className="gate-help" open>
        <summary>Where to find the code</summary>
        <ul>
          <li>
            Docker (<code>infra/personal</code>):{" "}
            <code>docker compose logs api | grep &quot;APPROVAL CODE&quot;</code>
          </li>
          <li>
            From source: the terminal running <code>cargo run -p helm-api</code>
          </li>
          <li>
            Look for <code>FIRST-DEVICE APPROVAL CODE: 123456 (valid 10 min)</code> and use the latest one.
          </li>
        </ul>
      </details>
      <label className="field">
        <span>Approval code</span>
        <input
          className="code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="000000"
          aria-invalid={state.error ? true : undefined}
          aria-describedby={state.error ? "gate-code-error" : undefined}
          value={code}
          disabled={state.submitting || limited}
          onChange={(ev) => {
            setCode(ev.target.value.replace(/\D/g, "").slice(0, 6));
          }}
        />
      </label>
      {state.error && (
        <p className="login-error" id="gate-code-error" role="alert">
          {codeErrorMessage(state.error)}
        </p>
      )}
      <button type="submit" className="btn btn-primary" disabled={state.submitting || limited}>
        {state.submitting ? "Checking…" : "Approve this device"}
      </button>
      <button type="button" className="btn btn-link" onClick={onNewCode} disabled={state.submitting}>
        Code expired? Get a new code
      </button>
    </form>
  );
}

function TrustedStep({ device }: { device: DeviceInfo }): React.JSX.Element {
  return (
    <div className="gate-form">
      <DeviceChip device={device} />
      <p>Approve it from a device you already use:</p>
      <ol className="gate-steps">
        <li>Open TradePrizm on that device.</li>
        <li>
          Go to <strong>Settings → Devices</strong> (on a phone: <strong>Account → Devices</strong>).
        </li>
        <li>
          Find <strong>{device.name}</strong> (id {device.id.slice(0, 8)}) and choose <strong>Approve</strong>
          .
        </li>
      </ol>
      <p className="muted gate-wait" role="status">
        <span className="spinner" aria-hidden="true" /> Waiting for approval — this page continues by itself.
      </p>
      <p className="muted">
        No other device at hand? Sign out and sign in with your email and password instead: TradePrizm emails
        a code that approves this device (a PIN sign-in cannot approve a device).
      </p>
    </div>
  );
}
